"use client";

import React from "react";
import BlogCards from "./blog-cards";
import ICMWrapper from "./icm-wrapper";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import { ICM_BLOGS_URL } from "@/app/utils/constants";
import { BlogData } from "../interface/homepage";
import { useRemoteConfig } from "@/app/hooks/use-remote-config";
import { InvestmentOptions } from "../interface/platform";
import { ArrowRightIcon } from "lucide-react";
import { cn } from "@/lib/utils";

type BlogsSectionProps = {
    showDownloadAppBtn?: boolean;
    handleDownloadAppClick?: () => void;
    page?: InvestmentOptions;
};

const BlogsSection: React.FC<BlogsSectionProps> = ({
    showDownloadAppBtn,
    handleDownloadAppClick,
    page,
}) => {
    const { data: blogData, loading: isLoading, error } = useRemoteConfig<BlogData[]>("blogs", []);

    if (isLoading) {
        return (
            <section className="bg-neutral-50 py-14 md:py-[120px]">
                <ICMWrapper>
                    <div className="animate-pulse">
                        <div className="h-8 w-48 rounded bg-gray-200"></div>
                        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="h-64 rounded bg-gray-200"></div>
                            ))}
                        </div>
                    </div>
                </ICMWrapper>
            </section>
        );
    }

    if (error) {
        return (
            <section className="bg-neutral-50 py-14 md:py-[120px]">
                <ICMWrapper>
                    <div className="text-center text-red-600">{error}</div>
                </ICMWrapper>
            </section>
        );
    }

    return (
        <section className="bg-neutral-50 py-14 md:py-[120px]" aria-labelledby="blog-section-title">
            <ICMWrapper>
                <div className="icm-container md:px-0">
                <div className="flex flex-col items-center justify-between align-middle md:flex-row">
                    <h2 id="blog-section-title" className="text-4xl font-semibold md:text-6xl">
                        From the blog
                    </h2>
                    <p
                        className={cn(
                            "hidden cursor-pointer text-lg font-medium text-neutral-500 md:flex md:items-center",
                            page !== InvestmentOptions.Home ? "underline" : "",
                        )}
                        aria-label="See more blogs"
                        onClick={() => (window.location.href = ICM_BLOGS_URL)}
                    >
                        {page === InvestmentOptions.Home ? (
                            <>
                                View all{" "}
                                <ArrowRightIcon className="ml-3 h-auto w-5 text-[#0F1016]" />
                            </>
                        ) : (
                            "See more"
                        )}
                    </p>
                </div>

                <div className="flex justify-start gap-[24px] overflow-hidden overflow-x-auto pt-6 scrollbar-hide sm:overflow-visible md:pt-[60px]">
                    {blogData.slice(0, 3).map((blog, index) => (
                        <BlogCards
                            key={index}
                            title={blog.title}
                            author={blog.label1}
                            date={blog.label2}
                            img={blog.imageUrl}
                            blogUrl={blog.blogUrl}
                            isLoading={isLoading}
                        />
                    ))}
                </div>

                <RegularButton
                    noIcon
                    showRightIcon={false}
                    size={ButtonSize.Medium}
                    variant={ButtonVariant.SecondaryDefault}
                    onClick={() => (window.location.href = ICM_BLOGS_URL)}
                    className="mt-1 block w-full max-w-full md:hidden md:max-w-fit"
                    label="See More"
                    aria-label="See more blogs"
                />

                {showDownloadAppBtn ? (
                    <div className="mt-12 hidden flex-col items-center justify-center gap-4 md:flex md:flex-row">
                        <RegularButton
                            label="Download the app"
                            variant={ButtonVariant.SecondaryDefault}
                            size={ButtonSize.Large}
                            noIcon={true}
                            showRightIcon={false}
                            className="w-full max-w-full border-neutralOpacityDark-30 hover:text-black md:max-w-fit"
                            onClick={() => {
                                if (typeof handleDownloadAppClick != "undefined") {
                                    handleDownloadAppClick();
                                }
                            }}
                        />
                    </div>
                ) : null}
                </div>
            </ICMWrapper>
        </section>
    );
};

export default BlogsSection;
